/**
 * @fileoverview Shared POD received / adjudicated status UI.
 *
 * Purpose: The creditors table, creditor slide-out, and preferred-creditor
 * (employee) slide-out all surface the same Recoveries-phase status, so the
 * badges and the Proof of Debt section live here rather than being copied.
 */

import { Link } from "@tanstack/react-router";
import { ChevronRight, Scale } from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import {
  POD_STATUS_COLORS,
  POD_STATUS_LABELS,
  derivePodOutcome,
  type PodClaimStatus,
  type PodStatusInfo,
} from "./podShared";

/**
 * "Received" is a milestone: an adjudicated claim was necessarily received.
 */
export function PodReceivedBadge({ status }: { status?: PodStatusInfo }) {
  if (!status) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const claimStatus = status.status as PodClaimStatus;
  const received = claimStatus === "received" || claimStatus === "adjudicated";
  return (
    <>
      <Badge
        color={
          received ? "emerald" : (POD_STATUS_COLORS[claimStatus] ?? "gray")
        }
        variant="soft"
        size="sm"
      >
        {received
          ? "Received"
          : (POD_STATUS_LABELS[claimStatus] ?? claimStatus)}
      </Badge>
      {status.matchedByName && (
        <p
          className="mt-1 text-[10px] leading-tight text-muted-foreground"
          title="The claim is not linked to this row — it was matched on name."
        >
          matched by name
        </p>
      )}
    </>
  );
}

export function PodAdjudicatedBadge({ status }: { status?: PodStatusInfo }) {
  if (!status) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const outcome = derivePodOutcome(status);
  if (!outcome) {
    return <span className="text-xs text-muted-foreground">Pending</span>;
  }
  return (
    <Badge color={outcome.color} variant="soft" size="sm">
      {outcome.label}
    </Badge>
  );
}

/**
 * Proof of Debt block for a detail slide-out. Shows received / adjudicated
 * status and links through to the claim in Recoveries when one exists.
 */
export function PodStatusSection({
  jobId,
  status,
}: {
  jobId: Id<"jobs">;
  status?: PodStatusInfo;
}) {
  return (
    <>
      <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]">
        Proof of Debt
      </div>
      <div className="flex items-start justify-between gap-4 py-2">
        <span className="text-sm text-muted-foreground">POD received</span>
        <div className="text-right">
          <PodReceivedBadge status={status} />
        </div>
      </div>
      <div className="flex items-start justify-between gap-4 py-2">
        <span className="text-sm text-muted-foreground">POD adjudicated</span>
        <div className="text-right">
          <PodAdjudicatedBadge status={status} />
        </div>
      </div>
      {status && (
        <Link
          to="/dashboard/jobs/$jobId/recoveries/$section"
          params={{ jobId, section: "proof-of-debt" }}
          search={{ claimId: status.claimId }}
          className="group mt-1 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Scale className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground">
              Open Proof of Debt claim
            </div>
            <div className="text-xs text-muted-foreground">
              View in Recoveries
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
        </Link>
      )}
    </>
  );
}
